import React, { useState, useEffect, useRef } from 'react';
import './App.css';

function AnimatedPrice({ value }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const duration = 900;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.floor(value * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
      else setDisplay(value);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);

  return new Intl.NumberFormat('ru-RU').format(display);
}

function App() {
  const [scannedItems, setScannedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [time, setTime] = useState(new Date());
  const barcodeBuffer = useRef('');
  const inactivityTimer = useRef(null);

  const fetchItemDetails = async (itemCode) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/get-item-details?itemCode=${itemCode}`);
      const data = await response.json();

      if (data.code === 200 && data.data && data.data.length > 0) {
        const item = data.data[0];
        setScannedItems(prev => [{
          ...item,
          scannedAt: new Date().toLocaleTimeString('ru-RU'),
          id: Date.now()
        }, ...prev]);
      } else {
        setError('Товар не найден');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Ошибка подключения к серверу');
    } finally {
      setIsLoading(false);
    }
  };

  const resetInactivityTimer = () => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
    }

    inactivityTimer.current = setTimeout(() => {
      setScannedItems([]);
      setError('');
    }, 120000);
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      if (e.key === 'Enter') {
        if (barcodeBuffer.current.length > 0) {
          fetchItemDetails(barcodeBuffer.current);
          resetInactivityTimer();
          barcodeBuffer.current = '';
        }
      } else if (e.key.length === 1) {
        barcodeBuffer.current += e.key;
      }
    };

    window.addEventListener('keypress', handleKeyPress);
    resetInactivityTimer();

    const clockTimer = setInterval(() => setTime(new Date()), 1000);

    return () => {
      window.removeEventListener('keypress', handleKeyPress);
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }
      clearInterval(clockTimer);
    };
  }, []);

  const formatDate = (d) => d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });
  const formatClock = (d) => d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="App">
      <div className="bg-orb bg-orb-1" aria-hidden="true"></div>
      <div className="bg-orb bg-orb-2" aria-hidden="true"></div>
      <div className="bg-grain" aria-hidden="true"></div>

      <header className="header">
        <div className="header-meta left">
          <span className="meta-label">Дата</span>
          <span className="meta-value">{formatDate(time)}</span>
        </div>
        <div className="logo-container">
          <img src="/logo.PNG" alt="Bob's Premium Market" className="logo" />
        </div>
        <div className="header-meta right">
          <span className="meta-label">Время</span>
          <span className="meta-value">{formatClock(time)}</span>
        </div>
      </header>

      <main className="main-content">
        {error && (
          <div className="error-message">
            <div className="error-icon">!</div>
            <div className="error-text">{error}</div>
            <button className="error-dismiss" onClick={() => setError('')}>Очистить</button>
          </div>
        )}

        {isLoading && (
          <div className="loading">
            <div className="spinner">
              <div className="spinner-inner"></div>
            </div>
            <p>Загрузка товара</p>
          </div>
        )}

        {scannedItems.length === 0 && !isLoading && !error && (
          <div className="welcome-message">
            <div className="scan-beacon" aria-hidden="true">
              <div className="beacon-orbit beacon-orbit-1"></div>
              <div className="beacon-orbit beacon-orbit-2"></div>
              <div className="beacon-ring beacon-ring-1"></div>
              <div className="beacon-ring beacon-ring-2"></div>
              <div className="beacon-ring beacon-ring-3"></div>
              <div className="beacon-ring beacon-ring-4"></div>
              <div className="beacon-sparkle beacon-sparkle-1"></div>
              <div className="beacon-sparkle beacon-sparkle-2"></div>
              <div className="beacon-sparkle beacon-sparkle-3"></div>
              <div className="beacon-sparkle beacon-sparkle-4"></div>
              <div className="beacon-core">
                <div className="beacon-scanline"></div>
                <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M4 7V5a1 1 0 0 1 1-1h2M4 17v2a1 1 0 0 0 1 1h2M20 7V5a1 1 0 0 0-1-1h-2M20 17v2a1 1 0 0 1-1 1h-2"/>
                  <line x1="7" y1="8" x2="7" y2="16"/>
                  <line x1="10" y1="8" x2="10" y2="16"/>
                  <line x1="13" y1="8" x2="13" y2="16"/>
                  <line x1="17" y1="8" x2="17" y2="16"/>
                </svg>
              </div>
            </div>
            <div className="welcome-eyebrow">
              <span className="eyebrow-line"></span>
              <span>Bob's Premium Market</span>
              <span className="eyebrow-line"></span>
            </div>
            <h2>Сканируйте<br/><em>штрих-код</em> товара</h2>
            <p>Поднесите товар к сканеру для проверки цены</p>
          </div>
        )}

        <div className="items-grid">
          {scannedItems.map((item, index) => (
            <article key={item.id || index} className="item-card">
              <div className="card-serial" aria-hidden="true">
                <span className="serial-glyph">№</span>
                <span className="serial-number">{String(index + 1).padStart(2, '0')}</span>
              </div>

              <div className="card-main">
                <div className="card-top">
                  <div className="card-eyebrow">
                    <span className="eyebrow-dot"></span>
                    <span>Наименование товара</span>
                  </div>
                  <div className="card-timestamp">
                    <span className="scan-dot"></span>
                    {item.scannedAt}
                  </div>
                </div>

                <h3 className="item-name">{item.itemName}</h3>

                <div className="card-meta">
                  <div className="meta-cell">
                    <span className="meta-cell-label">Артикул</span>
                    <span className="meta-cell-value">{item.itemCode}</span>
                  </div>
                </div>
              </div>

              <div className="card-price-section">
                <div className="price-ornament" aria-hidden="true">
                  <svg viewBox="0 0 40 40" width="40" height="40">
                    <circle cx="20" cy="20" r="3" fill="currentColor"/>
                    <circle cx="20" cy="20" r="10" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.5"/>
                    <circle cx="20" cy="20" r="18" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.3"/>
                  </svg>
                </div>
                <span className="price-label"><em>Цена</em></span>
                <div className="price-amount">
                  <span className="price-value">
                    <AnimatedPrice value={item.price} />
                  </span>
                </div>
                <span className="price-currency">сум</span>
              </div>
            </article>
          ))}
        </div>
      </main>
    </div>
  );
}

export default App;
